import CustomSnackBar from "@components/CustomSnackBar";
import {
  INITIAL_SNACKBAR_STATE,
  ISnackbarAlert,
  setErrorAlert,
  setInitialSnackbarState
} from "@constants/alerts";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Button,
  Grid,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { CustomClientModelVariable } from "@services/api/types";
import { CSVDataParsed, csvToMediaValues, isCsvContentValid } from "@utils/csv";
import { IVariableValues } from "@utils/external_effects";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCSVReader } from "react-papaparse";

export interface ModelVarsCsvReaderProps {
  enabled: boolean;
  modelVars: CustomClientModelVariable[];
  timePeriods: string[];
  templateFileName: string;
  modelHeader?: string;
  setModelVars: Dispatch<SetStateAction<CustomClientModelVariable[]>>;
  setVariableValues: Dispatch<SetStateAction<IVariableValues>>;
  children?: React.ReactNode;
}
const ModelVarsCsvReader = ({
  enabled,
  modelVars,
  timePeriods,
  templateFileName,
  modelHeader = "channel",
  setModelVars,
  setVariableValues,
  children
}: ModelVarsCsvReaderProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const { CSVReader } = useCSVReader();
  const [snackbarAlert, setSnackbarAlert] = useState<ISnackbarAlert>({
    ...INITIAL_SNACKBAR_STATE
  });
  const [csvVariableValues, setCsvVariableValues] = useState<IVariableValues>(
    {}
  );
  const [csvModelVars, setCsvModelVars] = useState<CustomClientModelVariable[]>(
    []
  );

  const onDeleteCSV = () => {
    const modelVarsOriginal = modelVars.filter(
      (modelVar) => !modelVar.is_custom
    );
    const mediaValues: {
      [key: string]: { [key: number]: number };
    } = {};

    timePeriods.forEach((timePeriod) => {
      mediaValues[timePeriod] = {};
      modelVarsOriginal.forEach((modelVar) => {
        mediaValues[timePeriod][modelVar.id] = 0;
      });
    });

    setCsvModelVars([...modelVarsOriginal]);
    setCsvVariableValues({ ...mediaValues });
  };

  const onDownloadTemplateCSV = () => {
    const modelVarsOriginal = modelVars.filter(
      (modelVar) => !modelVar.is_custom
    );

    const headerRow = [modelHeader, ...timePeriods];
    const contentRows = modelVarsOriginal.map((modelVar) => [
      modelVar.name,
      ...timePeriods.map((_) => 0)
    ]);

    const csvData = [headerRow, ...contentRows];
    const csvContent = csvData.map((row) => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = templateFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (Object.keys(csvVariableValues).length > 0) {
      setVariableValues(csvVariableValues);
    }
  }, [csvVariableValues, setVariableValues]);

  useEffect(() => {
    if (csvModelVars.length > 0) {
      setModelVars(csvModelVars);
    }
  }, [csvModelVars, setModelVars]);

  return (
    <Box className="w-full pl-10 pr-8 pb-4">
      <CustomSnackBar
        id={snackbarAlert.message}
        key={snackbarAlert.message}
        open={snackbarAlert.open}
        message={snackbarAlert.message}
        handleClose={() => setSnackbarAlert(setInitialSnackbarState())}
        autoHideDuration={snackbarAlert.autoHideDuration}
        color={snackbarAlert.severity}
      />
      <CSVReader
        onUploadAccepted={(results: any) => {
          const csvParsed = results as CSVDataParsed;

          if (!csvParsed.errors.length) {
            const csvContentStatus = isCsvContentValid(
              csvParsed.data,
              timePeriods,
              modelVars,
              modelHeader
            );
            if (
              !csvContentStatus.isHeaderValid ||
              !csvContentStatus.isModelVarsValid
            ) {
              if (!csvContentStatus.isHeaderValid) {
                setSnackbarAlert(
                  setErrorAlert(t("simulator.new_scenario.error_csv_weeks"))
                );
              }
              if (!csvContentStatus.isModelVarsValid) {
                setSnackbarAlert(
                  setErrorAlert(t("simulator.new_scenario.error_csv_media"))
                );
              }
            } else {
              const parsedData = csvToMediaValues(
                csvParsed.data,
                modelVars,
                theme.palette.warning.main
              );
              setCsvVariableValues(parsedData.data);
              setCsvModelVars(parsedData.mediaVars);
            }
          } else {
            setSnackbarAlert(
              setErrorAlert(t("simulator.new_scenario.error_importing_csv"))
            );
          }
        }}
      >
        {({
          getRootProps,
          acceptedFile,
          ProgressBar,
          getRemoveFileProps
        }: any) => (
          <>
            <Typography variant="h6" component="div" className="pb-2">
              {acceptedFile && acceptedFile.name}
            </Typography>
            <Grid
              container
              spacing={1}
              sx={{ paddingRight: isMobile ? 0 : isDesktop ? 4 : 8 }}
            >
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <Button
                  {...getRootProps()}
                  variant="outlined"
                  disabled={!enabled}
                >
                  <UploadFileIcon />
                  {t("simulator.new_scenario.import_csv")}
                </Button>
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                <Button
                  {...getRemoveFileProps()}
                  variant="outlined"
                  disabled={!acceptedFile}
                  onClick={(e) => {
                    const removeProps = getRemoveFileProps();
                    if (removeProps.onClick) {
                      removeProps.onClick(e);
                    }
                    onDeleteCSV();
                  }}
                >
                  <DeleteIcon color={!acceptedFile ? "disabled" : "error"} />
                  {t("simulator.new_scenario.delete_csv")}
                </Button>
              </Grid>
              <Grid
                size={{ xs: 12, sm: 6, md: 8 }}
                sx={{
                  textAlign: "right"
                }}
              >
                <Button
                  variant="outlined"
                  onClick={onDownloadTemplateCSV}
                  disabled={!enabled}
                  sx={{
                    width: isMobile ? "100%" : "auto"
                  }}
                >
                  <DownloadIcon />
                  {t("simulator.new_scenario.download_template_csv")}
                </Button>
              </Grid>
            </Grid>

            <ProgressBar
              style={{
                backgroundColor: theme.palette.primary.main,
                height: "1.0rem"
              }}
              className="mt-8 pr-4"
            />
            {children}
          </>
        )}
      </CSVReader>
    </Box>
  );
};

export default ModelVarsCsvReader;
